import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { LogoMark, LOGO_URL } from "@/components/brand/Logo";

const AuthSidePanel = () => (
  <div className="hidden lg:flex lg:w-[46%] xl:w-[42%] relative overflow-hidden" style={{ background: "#2A1810" }}>
    <div className="absolute inset-0 ccs-grain" />
    <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 text-[#F0E6D2] w-full">
      <div>
        <div className="flex items-center gap-3">
          <LogoMark size={52} testid="login-side-logo" />
          <div>
            <div className="font-display font-semibold text-xl">The CCS Approach</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F0E6D2]/55">Business Operating System</div>
          </div>
        </div>
      </div>
      <div className="space-y-6 max-w-md">
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">A Premium Business OS</div>
        <h2 className="font-display text-4xl xl:text-5xl font-semibold leading-[1.1] tracking-tight">
          Communicate.<br />Collaborate.<br />
          <span className="text-[#C28A2E]">Serve.</span>
        </h2>
        <p className="text-[#F0E6D2]/70 text-base leading-relaxed">
          The business operating system for facilitators, trainers, and consultants
          who deliver excellence in customer service, leadership, and team development.
        </p>
        <div className="grid grid-cols-3 gap-4 pt-4">
          {[
            { label: "Pipeline", value: "Track" },
            { label: "Workshops", value: "Manage" },
            { label: "AI", value: "Assist" },
          ].map((s) => (
            <div key={s.label} className="border-l-2 border-[#C28A2E]/50 pl-3">
              <div className="font-display text-xl font-semibold">{s.value}</div>
              <div className="text-xs text-[#F0E6D2]/55 uppercase tracking-wider mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-xs text-[#F0E6D2]/45">
        © {new Date().getFullYear()} The CCS Approach — Joylance Chapman-George
      </div>
    </div>
  </div>
);

const LoginPage = () => {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  if (user) return <Navigate to="/app/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FDFBF5]">
      <AuthSidePanel />
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md ccs-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <LogoMark size={42} testid="login-mobile-logo" />
            <div className="font-display font-semibold text-xl text-[#2A1810]">The CCS Approach</div>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#5D3A1F]/8 text-[#5D3A1F] text-xs font-medium mb-5">
            <Sparkles className="w-3 h-3" /> Welcome back
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-[#2A1810]">Sign in to your workspace</h1>
          <p className="text-[#6B5944] mt-2.5 text-base">Manage leads, clients, workshops and proposals — all in one place.</p>

          <form onSubmit={handleSubmit} className="space-y-5 mt-9" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-[#2A1810]">Email</Label>
              <Input
                id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ccsconsulting.com"
                className="h-12 rounded-xl border-[#EBE3D2] focus-visible:ring-[#5D3A1F] bg-white"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-[#2A1810]">Password</Label>
              <Input
                id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-12 rounded-xl border-[#EBE3D2] focus-visible:ring-[#5D3A1F] bg-white"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit" disabled={submitting} data-testid="login-submit-btn"
              className="w-full h-12 rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white font-medium text-base transition-all hover:-translate-y-[1px] hover:shadow-lg"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="text-sm text-[#6B5944] mt-7 text-center">
            New to CCS?{" "}
            <Link to="/signup" data-testid="link-signup" className="text-[#C28A2E] font-semibold hover:underline">
              Create an account
            </Link>
          </div>
          <div className="mt-6 pt-6 border-t border-[#EBE3D2] text-center space-y-2">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">For your clients</div>
            <Link to="/book" data-testid="link-public-booking" className="inline-block text-sm text-[#2A1810] hover:text-[#C28A2E] underline-offset-4 hover:underline">
              Public Booking Page → /book
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
